import { createSlice } from '@reduxjs/toolkit';
import {
  loadCameraClips,
  loadCameraStills,
  removeCameraClip,
  removeCameraStill,
  setCameraClipKeep,
} from '../thunks/cameraRecordings.thunks';

/** The key of a camera's day in the slice: `<cameraId>|<YYYY-MM-DD>`. */
export const recordingsKey = (cameraId, day) => `${cameraId}|${day}`;

const pending = (state, key, requestId) => {
  const entry = state[key];
  // A reload of the same day keeps its rows (and their places) while it loads.
  state[key] = {
    items: entry ? entry.items : [],
    status: entry && entry.status === 'loaded' ? 'refreshing' : 'loading',
    loadedAt: entry ? entry.loadedAt : null,
    error: null,
    requestId,
  };
};

const fulfilled = (state, key, requestId, items) => {
  const entry = state[key];
  if (!entry || entry.requestId !== requestId) return;
  state[key] = {
    items,
    status: 'loaded',
    loadedAt: Date.now(),
    error: null,
    requestId: null,
  };
};

const rejected = (state, key, requestId, error) => {
  const entry = state[key];
  if (!entry || entry.requestId !== requestId) return;
  entry.status = 'error';
  entry.error = error || { status: null, code: null };
  entry.requestId = null;
};

/**
 * A camera's recordings by day (camera-feeds plan, section 13.5): its motion
 * clips (`clips`, newest first) and its stills (`stills`, oldest first),
 * each keyed by recordingsKey. An entry is `{ items, status: 'loading' |
 * 'refreshing' | 'loaded' | 'error', loadedAt, error }`; `loadedAt` (epoch
 * ms) dates the signed thumbnail and picture URLs the items carry, which
 * expire about 10 minutes after the list came. A clip's MP4 URL is never
 * kept here: it is asked for per playback (which the API logs).
 */
const cameraRecordingsSlice = createSlice({
  name: 'cameraRecordings',
  initialState: {
    clips: {},
    stills: {},
  },
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(loadCameraClips.pending, (state, action) => {
        const { cameraId, day } = action.meta.arg;
        pending(
          state.clips,
          recordingsKey(cameraId, day),
          action.meta.requestId
        );
      })
      .addCase(loadCameraClips.fulfilled, (state, action) => {
        const { cameraId, day } = action.meta.arg;
        fulfilled(
          state.clips,
          recordingsKey(cameraId, day),
          action.meta.requestId,
          action.payload
        );
      })
      .addCase(loadCameraClips.rejected, (state, action) => {
        const { cameraId, day } = action.meta.arg;
        rejected(
          state.clips,
          recordingsKey(cameraId, day),
          action.meta.requestId,
          action.payload
        );
      })
      .addCase(loadCameraStills.pending, (state, action) => {
        const { cameraId, day } = action.meta.arg;
        pending(
          state.stills,
          recordingsKey(cameraId, day),
          action.meta.requestId
        );
      })
      .addCase(loadCameraStills.fulfilled, (state, action) => {
        const { cameraId, day } = action.meta.arg;
        fulfilled(
          state.stills,
          recordingsKey(cameraId, day),
          action.meta.requestId,
          action.payload
        );
      })
      .addCase(loadCameraStills.rejected, (state, action) => {
        const { cameraId, day } = action.meta.arg;
        rejected(
          state.stills,
          recordingsKey(cameraId, day),
          action.meta.requestId,
          action.payload
        );
      })
      // The API's clip, wherever the clip is listed. It keeps the listed
      // thumbnail when the answer brings none.
      .addCase(setCameraClipKeep.fulfilled, (state, action) => {
        const clip = action.payload;
        Object.values(state.clips).forEach((entry) => {
          entry.items = entry.items.map((item) =>
            item.id === clip.id
              ? {
                  ...item,
                  ...clip,
                  thumbnailUrl: clip.thumbnailUrl || item.thumbnailUrl,
                }
              : item
          );
        });
      })
      .addCase(removeCameraClip.fulfilled, (state, action) => {
        const clipId = action.payload;
        Object.values(state.clips).forEach((entry) => {
          entry.items = entry.items.filter((item) => item.id !== clipId);
        });
      })
      .addCase(removeCameraStill.fulfilled, (state, action) => {
        const stillId = action.payload;
        Object.values(state.stills).forEach((entry) => {
          entry.items = entry.items.filter((item) => item.id !== stillId);
        });
      });
  },
});

export default cameraRecordingsSlice.reducer;
